import React, { useRef, useState } from 'react'

import {
  ArrowPathIcon,
  BarsArrowDownIcon,
  BarsArrowUpIcon,
  ChatBubbleBottomCenterTextIcon,
} from '@heroicons/react/24/outline'

import ParentMessage from './ParentMessage'
import { setCurrentStreamingNotePage } from '@/client/generalSlice'
import { useAppDispatch, useAppSelector } from '@/client/hooks'
import Spinner from '@/components/Spinner'
import { Application } from '@/graphql/generated'
import { useGetStreamingNotesByApplicationQuery } from '@/services/streamingNote'

interface Thread {
  application: Application
  isLoadingApplication: boolean
  isFetchingApplication: boolean
  selectedUser: string
}

const SS_SCROLL_POS_Y_KEY = 'unlock:max:streaming-notes:scroll-pos-y'

export function Thread({
  application,
  isLoadingApplication,
  isFetchingApplication,
  selectedUser,
}: Thread) {
  const dispatch = useAppDispatch()
  const currentStreamingNotePage = useAppSelector((state) => state.general.currentStreamingNotePage)
  const messageListEl = useRef<HTMLUListElement>(null)
  const [page, setPage] = useState(() => currentStreamingNotePage)

  const {
    data: messages,
    isLoading,
    isFetching,
    isSuccess,
    refetch,
  } = useGetStreamingNotesByApplicationQuery(
    {
      page: currentStreamingNotePage,
      only_parent: true,
      application_id: application?.id,
      user_id: selectedUser,
    },
    { skip: !application?.id }
  )

  React.useEffect(() => {
    dispatch(setCurrentStreamingNotePage(page))
  }, [page])

  React.useEffect(() => {
    if (application?.id) {
      refetch()
    }
  }, [page, application?.id, refetch])

  React.useEffect(() => {
    messageListEl.current.addEventListener('scrollend', () => {
      sessionStorage.setItem(SS_SCROLL_POS_Y_KEY, messageListEl.current?.scrollTop.toString())
    })

    if (messageListEl.current) {
      // Restore scroll position from session storage
      const scrollY = sessionStorage.getItem?.(SS_SCROLL_POS_Y_KEY) ?? '0'
      messageListEl.current.scrollTop = parseInt(scrollY)
    }
  }, [isSuccess])

  return (
    <ul
      className='flex gap-1 flex-grow flex-col-reverse pb-3 overflow-y-auto'
      ref={messageListEl}
    >
      {/**
       * You might be wondering... Why is this damn list upside-down?
       * Well, that's because we display the most recent message at the bottom.
       */}
      <li className='flex w-full justify-center join'>
        {isLoading || isFetching || isLoadingApplication || isFetchingApplication ? (
          <Spinner size={6} />
        ) : (
          <>
            <button
              type='button'
              className='join-item btn btn-xs'
              disabled={!messages?.previous}
              onClick={() => setPage(page - 1)}
            >
              ↓ Newer
            </button>
            <button
              type='button'
              className='join-item btn btn-xs'
              onClick={() => {
                const scrollHeight = messageListEl.current.scrollHeight
                messageListEl.current.scrollTop = -scrollHeight
              }}
            >
              <BarsArrowUpIcon
                height={16}
                width={16}
              />{' '}
              Scroll to top
            </button>
            <button
              type='button'
              className='join-item btn btn-xs'
              onClick={refetch}
              data-test='refresh-notes'
            >
              <ArrowPathIcon
                height={14}
                width={14}
              />{' '}
              Refresh
            </button>
          </>
        )}
      </li>

      {!isLoading && messages?.count === 0 && (
        <li className='text-center'>
          <div className='inline-flex gap-1 text-gray-400'>
            <ChatBubbleBottomCenterTextIcon
              height={22}
              width={22}
            />
            <p>This deal has no notes yet.</p>
          </div>
        </li>
      )}

      {!(isLoading || isLoadingApplication || isFetchingApplication) &&
        messages?.results.map((message) => (
          <ParentMessage
            key={message.uuid}
            message={message}
            applicationExternalId={application?.externalId}
          />
        ))}

      <li className='join justify-center  mb-3'>
        {isSuccess && (
          <>
            <button
              className='join-item btn btn-xs'
              disabled={!messages?.next}
              onClick={() => setPage(page + 1)}
            >
              ↑ Older
            </button>

            <button
              className='join-item btn btn-xs'
              onClick={() => (messageListEl.current.scrollTop = 0)}
            >
              <BarsArrowDownIcon
                height={16}
                width={16}
              />{' '}
              Scroll to bottom
            </button>
          </>
        )}
      </li>
    </ul>
  )
}
